/**
 * Sandbox Chat View
 *
 * Full-width chat interface for interacting with a running sandbox instance.
 * Uses the shared ChatMessage / ChatComposer / ToolCall components
 * from @chatsdet/ui, following the shared chat panel pattern.
 *
 * @module features/sandboxes/components/SandboxChatView
 */

import React from 'react';
import {
  Stack,
  ChatMessage,
  ChatMessageList,
  ChatComposer,
  Markdown,
  ToolCall,
  Text,
  groupToolCallParts,
  type ToolCallState,
  useToast,
} from '@chatsdet/ui';
import { CircleNotch, Cube } from '@phosphor-icons/react';
import { useSandboxAgent, type SandboxAgentMessage } from '../../../hooks/useSandboxAgent';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type SandboxChatViewProps = {
  instanceId: string;
  isEnabled: boolean;
  /** Ref that exposes a reload function for the parent to trigger chat refresh */
  reloadRef?: React.MutableRefObject<(() => void) | null>;
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Map AI SDK message part type guards */
function isTextPart(part: { type: string }): part is { type: 'text'; text: string } {
  return (
    part.type === 'text' && 'text' in part && typeof (part as { text?: unknown }).text === 'string'
  );
}

function isToolPart(part: { type: string }): part is {
  type: string;
  toolName?: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  preliminary?: boolean;
} {
  return part.type.startsWith('tool-') || part.type === 'dynamic-tool';
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  bash: 'Terminal',
  read_file: 'Read File',
  write_file: 'Write File',
  list_directory: 'List Directory',
  propose_template_update: 'Propose Update',
  report_authoring_complete: 'Report Complete',
};

function getToolName(part: { type: string; toolName?: string }): string {
  if (part.type === 'dynamic-tool') return part.toolName ?? 'unknown';
  if (part.type.startsWith('tool-')) return part.type.slice(5);
  return 'unknown';
}

function mapToolState(state?: string): ToolCallState {
  switch (state) {
    case 'input-streaming':
    case 'input-available':
      return 'pending';
    case 'output-error':
      return 'error';
    case 'output-available':
    default:
      return 'success';
  }
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const SandboxChatView: React.FC<SandboxChatViewProps> = ({
  instanceId,
  isEnabled,
  reloadRef,
}) => {
  const { toast } = useToast();
  const { messages, input, isLoading, isLoadingHistory, sendMessage, stop, setInput, reload } =
    useSandboxAgent({
      instanceId: isEnabled ? instanceId : null,
      onError: (error) => {
        toast.error(error.message, { reportError: false });
      },
    });

  // Expose reload to parent via ref
  React.useEffect(() => {
    if (reloadRef) reloadRef.current = reload;
    return () => {
      if (reloadRef) reloadRef.current = null;
    };
  }, [reload, reloadRef]);

  const composerPlaceholder = isEnabled
    ? 'Ask the agent to modify your environment...'
    : 'Waiting for sandbox to be ready...';

  const composerHelperText = !isEnabled ? 'Sandbox is not ready yet.' : undefined;

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <ChatMessageList className="flex-1 p-3" isStreaming={isLoading}>
          {isLoadingHistory ? (
            <LoadingState />
          ) : messages.length === 0 ? (
            <EmptyState />
          ) : (
            messages.map((message) => <MessageRenderer key={message.id} message={message} />)
          )}
        </ChatMessageList>
      </div>

      {/* Composer */}
      <div className="border-t border-border flex-shrink-0">
        <ChatComposer
          value={isEnabled ? input : ''}
          onChange={isEnabled ? setInput : () => {}}
          onSubmit={() => void sendMessage()}
          onStop={stop}
          isSending={isLoading}
          submitLabel="Send"
          stopLabel="Stop"
          placeholder={composerPlaceholder}
          isDisabled={!isEnabled}
          disabledHelperText={composerHelperText}
          className="border-none rounded-none shadow-none"
        />
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Empty / Loading States
// -----------------------------------------------------------------------------

const LoadingState: React.FC = () => (
  <div className="flex h-full items-center justify-center">
    <div className="text-center">
      <Cube size={32} className="mx-auto mb-3 text-muted-foreground/50 animate-pulse" />
      <Text size="sm" className="text-muted-foreground">
        Loading chat history...
      </Text>
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex h-full items-center justify-center">
    <div className="max-w-[280px] text-center px-4">
      <Cube size={32} className="mx-auto mb-3 text-muted-foreground/50" />
      <Text size="sm" className="text-muted-foreground mb-2">
        Ask the agent to help with your sandbox
      </Text>
      <Text size="xs" className="text-muted-foreground/70">
        For example: &quot;Install dependencies and start the dev server&quot;
      </Text>
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// Message Renderer
// -----------------------------------------------------------------------------

type MessageRendererProps = {
  message: SandboxAgentMessage;
};

const MessageRenderer = React.memo<MessageRendererProps>(function MessageRenderer({ message }) {
  const parts = message.parts ?? [];
  const taggedParts: Array<{ key: string; node: React.ReactNode; isTool: boolean }> = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;

    if (isTextPart(part) && part.text.trim()) {
      taggedParts.push({
        key: `text-${i}`,
        isTool: false,
        node: (
          <Markdown key={`text-${i}`} className="prose prose-sm max-w-none text-foreground" compact>
            {part.text}
          </Markdown>
        ),
      });
    }

    if (isToolPart(part)) {
      const toolName = getToolName(part);
      const state = part.preliminary ? 'pending' : mapToolState(part.state);
      const key = part.toolCallId ?? `tool-${i}`;

      taggedParts.push({
        key,
        isTool: true,
        node: (
          <ToolCall
            key={key}
            toolName={toolName}
            displayName={TOOL_DISPLAY_NAMES[toolName] ?? toolName}
            state={state}
            input={part.input}
            output={part.output}
            error={part.errorText}
            grouped
          />
        ),
      });
    }
  }

  const renderedParts = groupToolCallParts(taggedParts);

  // Thinking indicator for empty assistant messages
  if (renderedParts.length === 0) {
    if (message.role === 'assistant') {
      renderedParts.push(
        <div key="thinking" className="flex items-center gap-2 text-xs text-muted-foreground">
          <CircleNotch size={12} className="animate-spin" />
          <span>Thinking...</span>
        </div>
      );
    } else {
      return null;
    }
  }

  // History messages carry a server-resolved author in metadata ("You" for the
  // viewer's own turns, the teammate's name otherwise). Label only teammates'
  // turns so the viewer's own turns read the same whether loaded or sent in-session.
  const metadataAuthor = (message.metadata as { author?: string } | undefined)?.author;
  const author = metadataAuthor === 'You' ? undefined : metadataAuthor;

  return (
    <ChatMessage role={message.role as 'user' | 'assistant'} author={author} className="px-1">
      <Stack gap="sm">{renderedParts}</Stack>
    </ChatMessage>
  );
});
